import ChoiceList from "components/ChoiceList";
import { WizardTextField, ZipField } from "components/WizardFields";

import type { Answers, RelationType, WhereType } from "../answers";
import { RELATION_OPTIONS, WHERE_OPTIONS } from "../options";
import {
  nameError,
  relationOtherTextError,
  zipError,
  isValidReference,
} from "../validation";
import YesNoChoice from "./YesNoChoice";
import StepFrame, { gated, useContinueGate } from "./StepFrame";
import type { StepProps } from "./types";

const READY_OPTIONS = [
  {
    value: "yes" as const,
    label: "Yes",
    caption: "We’ll be ready to arrange transportation as soon as we talk.",
  },
  {
    value: "no" as const,
    label: "Not yet",
    caption: "That’s okay. Nothing happens until you tell us.",
  },
];

export function WhoPassedStep(props: StepProps) {
  const { answers, update, onBack } = props;
  const { attempted, tryContinue } = useContinueGate(props, "who-passed");
  const setName = (key: "firstName" | "lastName", value: string) =>
    update((current) => ({
      ...current,
      decedent: { ...current.decedent, [key]: value },
    }));
  return (
    <StepFrame
      answers={answers}
      step="who-passed"
      onBack={onBack}
      onContinue={tryContinue}
    >
      <div className="grid grid-cols-1 gap-4 2xs:grid-cols-2">
        <WizardTextField
          id="decedent-first-name"
          label="First name"
          value={answers.decedent.firstName}
          onChange={(value) => setName("firstName", value)}
          error={gated(
            attempted,
            nameError(answers.decedent.firstName, "First name"),
          )}
          autoComplete="off"
          autoFocus
        />
        <WizardTextField
          id="decedent-last-name"
          label="Last name"
          value={answers.decedent.lastName}
          onChange={(value) => setName("lastName", value)}
          error={gated(
            attempted,
            nameError(answers.decedent.lastName, "Last name"),
          )}
          autoComplete="off"
        />
      </div>
    </StepFrame>
  );
}

export function WhereStep(props: StepProps) {
  const { answers, update, onBack, checking } = props;
  const { attempted, tryContinue } = useContinueGate(props, "where");
  const first = answers.decedent.firstName.trim();
  const setWhere = (change: Partial<Answers["where"]>) =>
    update((current) => ({
      ...current,
      where: { ...current.where, ...change },
    }));
  return (
    <StepFrame
      answers={answers}
      step="where"
      onBack={onBack}
      onContinue={tryContinue}
      busy={checking}
    >
      <div className="flex flex-col gap-5">
        <ChoiceList<WhereType>
          label="Where they are right now"
          options={WHERE_OPTIONS}
          value={answers.where.type}
          onChoose={(type) => setWhere({ type })}
        />
        <ZipField
          id="where-zip"
          label={
            first === ""
              ? "ZIP code where they are"
              : `ZIP code where ${first} is`
          }
          value={answers.where.zip}
          onChange={(zip) => setWhere({ zip })}
          error={gated(attempted, zipError(answers.where.zip))}
        />
        {attempted && answers.where.type === null && (
          <p className="text-sm font-medium text-red-700">
            Please choose where they are.
          </p>
        )}
      </div>
    </StepFrame>
  );
}

/** The relation question, for the death flow and for an imminent loved
 * one. `choose` writes into whichever answer the step owns. */
export function RelationFields({
  label,
  type,
  otherText,
  attempted,
  onChange,
  advanceSoon,
}: {
  label: string;
  type: RelationType | null;
  otherText: string;
  attempted: boolean;
  onChange: (relation: {
    type: RelationType | null;
    otherText: string;
  }) => void;
  advanceSoon: () => void;
}) {
  return (
    <>
      <ChoiceList<RelationType>
        label={label}
        options={RELATION_OPTIONS}
        value={type}
        onChoose={(next) => {
          onChange({ type: next, otherText });
          if (next !== "other") advanceSoon();
        }}
        other={{
          forValue: "other",
          label: "How are you related?",
          value: otherText,
          onChange: (text) => onChange({ type, otherText: text }),
          error:
            gated(attempted, relationOtherTextError(otherText)) ?? undefined,
          maxLength: 80,
        }}
      />
      {attempted && type === null && (
        <p className="mt-3 text-sm font-medium text-red-700">
          Please choose one.
        </p>
      )}
    </>
  );
}

export function RelationStep(props: StepProps) {
  const { answers, update, onBack, advanceSoon } = props;
  const { attempted, tryContinue } = useContinueGate(props, "relation");
  return (
    <StepFrame
      answers={answers}
      step="relation"
      onBack={onBack}
      onContinue={tryContinue}
      continueDisabled={answers.relation.type === null}
    >
      <RelationFields
        label="How you are related"
        type={answers.relation.type}
        otherText={answers.relation.otherText}
        attempted={attempted}
        onChange={(relation) => update((current) => ({ ...current, relation }))}
        advanceSoon={advanceSoon}
      />
    </StepFrame>
  );
}

function readyValue(ready: boolean | null): "yes" | "no" | null {
  if (ready === null) return null;
  return ready ? "yes" : "no";
}

export function ReadyStep({
  answers,
  update,
  advance,
  advanceSoon,
  onBack,
}: StepProps) {
  const value = readyValue(answers.readyForCare);
  return (
    <StepFrame
      answers={answers}
      step="ready"
      onBack={onBack}
      onContinue={advance}
      continueDisabled={answers.readyForCare === null}
    >
      <ChoiceList
        label="Ready for us to bring them into our care"
        options={READY_OPTIONS}
        value={value}
        onChoose={(choice) => {
          update((current) => ({ ...current, readyForCare: choice === "yes" }));
          advanceSoon();
        }}
      />
    </StepFrame>
  );
}

export function PreplanStep(props: StepProps) {
  const { answers, update, advanceSoon, onBack } = props;
  const { attempted, tryContinue } = useContinueGate(props, "preplan");
  const first = answers.decedent.firstName.trim();
  const referenceInvalid = !isValidReference(answers.preplanReference);
  return (
    <StepFrame
      answers={answers}
      step="preplan"
      onBack={onBack}
      onContinue={tryContinue}
      continueDisabled={answers.preplanned === null}
    >
      <YesNoChoice
        label={
          first === ""
            ? "Pre-planned with After"
            : `${first} pre-planned with After`
        }
        value={answers.preplanned}
        onChoose={(preplanned) => {
          update((current) => ({ ...current, preplanned }));
          if (!preplanned) advanceSoon();
        }}
      />
      {answers.preplanned === true && (
        <div className="mt-6">
          <WizardTextField
            id="preplan-reference"
            label="Policy or plan ID"
            optional
            value={answers.preplanReference}
            onChange={(preplanReference) =>
              update((current) => ({ ...current, preplanReference }))
            }
            error={
              attempted && referenceInvalid
                ? "Please keep this to 64 characters"
                : null
            }
            helper={`Don’t have it handy? Skip it. We’ll find ${first === "" ? "their" : `${first}’s`} plan by name.`}
            maxLength={64}
            autoComplete="off"
          />
        </div>
      )}
      <p className="mt-6 text-base leading-normal text-oat-700">
        We also help people arrange things ahead of time, so their families have
        less to decide later.{" "}
        <a
          href="https://www.after.com/prearrange"
          target="_blank"
          rel="noreferrer"
          className="font-bold text-evergreen underline underline-offset-4 hover:text-plum"
        >
          See how it works
        </a>
      </p>
    </StepFrame>
  );
}
